import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useConversations } from '../hooks/useMessaging';
import {
  LayoutDashboard,
  Users,
  Target,
  Building2,
  Calendar,
  LogOut,
  Menu,
  X,
  Search,
  Briefcase,
  Bell,
  MessageSquare,
  Settings,
  Shield,
  Mail,
  UserPlus,
  Sparkles,
  FileText,
  Rss,
  BarChart3,
  RefreshCw,
  Zap,
  Flame,
  FileUp,
  Database,
  FileStack,
  BookOpen,
} from 'lucide-react';
import { useState } from 'react';
import NotificationBell from './NotificationBell';

// Seller navigation
const sellerNavigation = [
  { name: 'Dashboard', href: '/seller/dashboard', icon: LayoutDashboard },
  { name: 'BD Resources', href: '/seller/bd-resources', icon: BookOpen },
  { name: 'Company Updates', href: '/seller/posts', icon: FileText },
  { name: 'Buyer Interest', href: '/seller/interest', icon: Users },
  { name: 'Smart Matching', href: '/seller/matching', icon: Target },
  { name: 'Company Profile', href: '/seller/profile', icon: Building2 },
  { name: 'Messages', href: '/seller/messages', icon: MessageSquare },
  { name: 'Notifications', href: '/seller/notifications', icon: Bell },
  { name: 'Settings', href: '/seller/settings', icon: Settings },
];

// Buyer navigation
const buyerNavigation = [
  { name: 'Dashboard', href: '/buyer/dashboard', icon: LayoutDashboard },
  { name: 'Seller Feed', href: '/buyer/feed', icon: Rss },
  { name: 'Search Sellers', href: '/buyer/search', icon: Search },
  { name: 'Smart Matches', href: '/buyer/matches', icon: Sparkles },
  { name: 'Messages', href: '/buyer/messages', icon: MessageSquare },
  { name: 'Company Profile', href: '/buyer/profile', icon: Building2 },
  { name: 'Settings', href: '/buyer/settings', icon: Settings },
];

// Admin navigation
const adminNavigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'BD Analytics', href: '/admin/analytics', icon: BarChart3 },
  { name: 'Introductions', href: '/admin/introductions', icon: UserPlus },
  { name: 'Sellers', href: '/admin/sellers', icon: Building2 },
  { name: 'Buyers', href: '/admin/buyers', icon: Briefcase },
  { name: 'Seller Posts', href: '/admin/posts', icon: MessageSquare },
  { name: 'Crawler Status', href: '/admin/crawler', icon: RefreshCw },
  { name: 'Signals', href: '/admin/signals', icon: Zap },
  { name: 'BD Deals', href: '/admin/deals', icon: FileStack },
  { name: 'Catalysts', href: '/admin/catalysts', icon: Calendar },
  { name: 'Data Health', href: '/admin/hot-areas', icon: Database },
  { name: 'Signal Parser', href: '/admin/signal-parser', icon: FileUp },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Email Settings', href: '/admin/email', icon: Mail },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export default function Layout({ userType = 'seller' }) {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get unread message count
  const userId = userType === 'seller' ? userProfile?.seller_id : userProfile?.buyer_id;
  const { unreadCount } = useConversations(userId, userType);

  const navigation = userType === 'admin'
    ? adminNavigation
    : userType === 'buyer'
      ? buyerNavigation
      : sellerNavigation;

  // Get company name from profile
  const companyName = userType === 'admin'
    ? 'Pharmunity Admin'
    : userType === 'buyer'
      ? userProfile?.buyers?.company_name
      : userProfile?.sellers?.company_name;

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
              <img
                src="/logo.png"
                alt="Pharmunity"
                className="h-10 w-10 object-cover scale-[1.35]"
              />
            </div>
            <div>
              <span className="font-semibold text-gray-900">Pharmunity</span>
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                userType === 'admin'
                  ? 'bg-red-100 text-red-700'
                  : userType === 'buyer'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'
              }`}>
                {userType === 'admin' ? 'Admin' : userType === 'buyer' ? 'Buyer' : 'Seller'}
              </span>
            </div>
          </div>
          <button
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isMessages = item.name === 'Messages';
            const showBadge = isMessages && unreadCount > 0;

            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? userType === 'buyer'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-purple-50 text-purple-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.name}</span>
                {showBadge && (
                  <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-bold rounded-full ${
                    userType === 'buyer'
                      ? 'bg-blue-600 text-white'
                      : 'bg-purple-600 text-white'
                  }`}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User section - fixed at bottom */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              userType === 'buyer' ? 'bg-blue-100' : 'bg-purple-100'
            }`}>
              <span className={`font-medium ${
                userType === 'buyer' ? 'text-blue-700' : 'text-purple-700'
              }`}>
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {companyName || 'My Company'}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" /> {/* Spacer to push content to right */}

            <div className="flex items-center gap-4">
              {/* Notification Bell - only for sellers */}
              {userType === 'seller' && (
                <NotificationBell />
              )}

              {userType === 'seller' && (
                <span className="text-sm text-gray-500">
                  Subscription:{' '}
                  <span className={`font-medium capitalize ${
                    userProfile?.subscription_tier === 'enterprise' ? 'text-purple-600' :
                    userProfile?.subscription_tier === 'pro' ? 'text-blue-600' :
                    'text-gray-600'
                  }`}>
                    {userProfile?.subscription_tier || 'Free'}
                  </span>
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
